import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import type { RootContent, PhrasingContent } from "mdast";

export type Block =
  | { id: string; type: "text"; text: string; source?: string }
  | {
      id: string;
      type: "check";
      text: string;
      checked: boolean;
      source?: string;
    }
  | { id: string; type: "image"; assetId: string; alt: string };
export const newText = (text = ""): Block => ({
  id: crypto.randomUUID(),
  type: "text",
  text,
});
const parser = unified().use(remarkParse).use(remarkGfm);
const assetPrefix = "matomemo-asset://";

// Unedited legacy paragraphs keep their original formatting on the wire.
// New input is escaped, so typing Markdown punctuation always produces literal text.
export function decodeBody(body: string): Block[] {
  const tree = parser.parse(body);
  const blocks: Block[] = [];
  const definitions = new Map(
    tree.children
      .filter((n) => n.type === "definition")
      .map((n) => [n.identifier, n.url]),
  );
  function plain(node: RootContent | PhrasingContent): string {
    if (node.type === "break") return "\n";
    if (node.type === "image") return node.alt || "画像";
    if (node.type === "link") {
      const label = node.children.map(plain).join("");
      return label === node.url ? label : `${label} (${node.url})`;
    }
    if (node.type === "linkReference")
      return `${node.children.map(plain).join("")} (${definitions.get(node.identifier) ?? node.identifier})`;
    if ("value" in node) return node.value;
    if ("children" in node)
      return node.children
        .map((child) => plain(child as RootContent))
        .join(
          node.type === "tableRow" ? "　" : node.type === "table" ? "\n" : "",
        );
    return "";
  }
  function visit(node: RootContent) {
    if (node.type === "definition") return;
    if (
      node.type === "list" &&
      node.children.every((item) => item.checked == null) &&
      !containsImage(node)
    ) {
      blocks.push({
        ...newText(node.children.map((item) => `・${plain(item)}`).join("\n")),
        source: containsReference(node)
          ? undefined
          : body.slice(node.position?.start.offset, node.position?.end.offset),
      } as Block);
      return;
    }
    if (node.type === "list" || node.type === "blockquote") {
      node.children.forEach((child) => visit(child as RootContent));
      return;
    }
    if (node.type === "listItem") {
      const first = node.children[0];
      if (typeof node.checked === "boolean" && first && !containsImage(first)) {
        blocks.push({
          id: crypto.randomUUID(),
          type: "check",
          text: plain(first) === "\u200b" ? "" : plain(first),
          checked: node.checked,
        });
        node.children.slice(1).forEach(visit);
      } else node.children.forEach(visit);
      return;
    }
    if (node.type === "image" || node.type === "imageReference") {
      const url =
        node.type === "image"
          ? node.url
          : (definitions.get(node.identifier) ?? "");
      if (url.startsWith(assetPrefix))
        blocks.push({
          id: crypto.randomUUID(),
          type: "image",
          assetId: url.slice(assetPrefix.length),
          alt: node.alt ?? "画像",
        });
      else blocks.push(newText(`${node.alt || "画像"} (${url})`));
      return;
    }
    if ("children" in node && containsImage(node)) {
      let text = "";
      for (const child of node.children) {
        if (containsImage(child as RootContent)) {
          if (text) blocks.push(newText(text));
          text = "";
          visit(child as RootContent);
        } else text += plain(child as RootContent);
      }
      if (text) blocks.push(newText(text));
      return;
    }
    const text = plain(node);
    if (text)
      blocks.push({
        ...newText(text),
        source: containsReference(node)
          ? undefined
          : body.slice(node.position?.start.offset, node.position?.end.offset),
      } as Block);
  }
  tree.children.forEach(visit);
  return blocks.length ? blocks : [newText()];
}
function containsImage(node: RootContent): boolean {
  return (
    node.type === "image" ||
    node.type === "imageReference" ||
    ("children" in node &&
      node.children.some((child) => containsImage(child as RootContent)))
  );
}
function escapeText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/[\\`*_{}\[\]()#+\-.!<>~|]/g, "\\$&")
    .replace(/:/g, "&#58;")
    .replace(/\n/g, "  \n");
}
export function encodeBody(blocks: Block[]): string {
  return blocks
    .map((block) => {
      if (block.type === "image")
        return `![${block.alt.replace(/[\[\]\\\r\n]/g, "")}](matomemo-asset://${block.assetId})`;
      if (block.source !== undefined) return block.source;
      if (block.type === "check")
        return `- [${block.checked ? "x" : " "}] ${(block.text ? escapeText(block.text) : "&#8203;").replace(/\n/g, "\n  ")}`;
      return escapeText(block.text);
    })
    .join("\n\n");
}

function containsReference(node: RootContent): boolean {
  return (
    node.type === "linkReference" ||
    ("children" in node &&
      node.children.some((child) => containsReference(child as RootContent)))
  );
}
